/**
 * Application sidebar navigation
 *
 * Roles: admin, user, manager
 * Divisions: HR, Finance (and others)
 */

import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { useAuth } from '@/core/auth/AuthContext'

interface NavItem {
  name: string
  path: string
  icon: JSX.Element
  // Roles: admin, user, manager
  requiredRoles?: string[]
  // Divisions: HR, Finance, etc.
  requiredDivisions?: string[]
  // Requires user to have direct reports (be a manager of someone)
  requiresDirectReports?: boolean
  // Group label to display before this item (for visual grouping)
  groupLabel?: string
}

interface NavGroup {
  name: string
  icon: JSX.Element
  basePath: string
  items: NavItem[]
}

type NavEntry = NavItem | NavGroup

const isNavGroup = (entry: NavEntry): entry is NavGroup => {
  return 'items' in entry
}

const navEntries: NavEntry[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    name: 'Leave',
    basePath: '/leave',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    items: [
      {
        name: 'My Requests',
        path: '/leave',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
      {
        name: 'My Balance',
        path: '/leave/balance',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      },
      {
        name: 'Leave Calendar',
        path: '/leave/calendar',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        name: 'Approvals',
        path: '/leave/approve',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        requiresDirectReports: true,
        requiredDivisions: ['Human Resources'],
        groupLabel: 'Manager',
      },
      {
        name: 'All Employees Balances',
        path: '/leave/manage',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        requiredRoles: ['admin'],
        requiredDivisions: ['Human Resources'],
        groupLabel: 'Admin / HR',
      },
      {
        name: 'Leave Types',
        path: '/leave/types',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        ),
        requiredRoles: ['admin'],
        requiredDivisions: ['Human Resources'],
      },
      {
        name: 'Public Holidays',
        path: '/leave/holidays',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        requiredRoles: ['admin'],
        requiredDivisions: ['Human Resources'],
      },
      {
        name: 'Reports',
        path: '/leave/reports',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
        requiredDivisions: ['Human Resources'],
      },
    ],
  },
  {
    name: 'Learning',
    basePath: '/learning',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    items: [
      {
        name: 'My Records',
        path: '/learning/my-records',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        ),
      },
      {
        name: 'My Certifications',
        path: '/learning/certifications',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        ),
      },
      {
        name: 'Training Repository',
        path: '/learning/trainings',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        ),
      },
      {
        name: 'Dashboard',
        path: '/learning/reports',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
        requiredDivisions: ['Human Resources'],
        groupLabel: 'Admin / HR',
      },
      {
        name: 'All Training Records',
        path: '/learning/all-records',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
        requiredDivisions: ['Human Resources'],
      },
      {
        name: 'By Employee',
        path: '/learning/employees',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
        requiredDivisions: ['Human Resources'],
      },
      {
        name: 'Categories',
        path: '/learning/categories',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        ),
        requiredRoles: ['admin'],
        requiredDivisions: ['Human Resources'],
      },
      {
        name: 'Requirements',
        path: '/learning/requirements',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
        requiredDivisions: ['Human Resources'],
      },
    ],
  },
  // Reimbursement module hidden - not deployed yet
  // {
  //   name: 'Reimbursement',
  //   basePath: '/reimbursement',
  //   ...
  // },
  {
    name: 'Employee Info',
    basePath: '/employee-info',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    items: [
      {
        name: 'My Profile',
        path: '/employee-info',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      {
        name: 'Staff Directory',
        path: '/directory',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
      },
      {
        name: 'Employee Directory',
        path: '/employee-info/directory',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
        requiredDivisions: ['Human Resources'],
        groupLabel: 'Admin / HR',
      },
      {
        name: 'Org Chart',
        path: '/employee-info/org-chart',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        ),
      },
    ],
  },
  {
    name: 'Lunch Expense',
    path: '/lunch-expense',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    name: 'Penilaian Individu',
    basePath: '/penilaian',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    items: [
      {
        name: 'Tugas Saya',
        path: '/penilaian/my-assessments',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
      },
      {
        name: 'KPI Saya',
        path: '/penilaian/my-kpis',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        ),
      },
      {
        name: 'Nilai Saya',
        path: '/penilaian/my-result',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
        ),
      },
      {
        name: 'KPI Tim',
        path: '/penilaian/team-kpis',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
        requiredRoles: ['admin', 'manager'],
        groupLabel: 'Manajer',
      },
      {
        name: 'Nilai Tim',
        path: '/penilaian/team-results',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        requiredRoles: ['admin', 'manager'],
      },
      {
        name: 'Periode',
        path: '/penilaian/periods',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
        groupLabel: 'Admin / HR',
      },
      {
        name: 'Penugasan',
        path: '/penilaian/assignments',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
      },
      {
        name: 'Manajemen KPI',
        path: '/penilaian/kpis',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
      },
      {
        name: 'Rencana KPI',
        path: '/penilaian/kpi-plans',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
      },
      {
        name: 'Revisi KPI',
        path: '/penilaian/kpi-revision',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
      },
      {
        name: 'Semua Hasil',
        path: '/penilaian/results',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
      },
      {
        name: 'Master Data',
        path: '/penilaian/master-data',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        ),
        requiredRoles: ['admin', 'hr'],
      },
    ],
  },
  {
    name: 'Admin',
    path: '/admin',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    requiredRoles: ['admin'],
  },
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const location = useLocation()
  const { user } = useAuth()
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    // Auto-expand groups that contain the current path
    const expanded: string[] = []
    navEntries.forEach((entry) => {
      if (isNavGroup(entry) && location.pathname.startsWith(entry.basePath)) {
        expanded.push(entry.name)
      }
    })
    return expanded
  })

  // Check if user has required role (admin, user, manager)
  const hasRole = (roleName: string) => {
    if (!user?.roles) return false
    return user.roles.some((role) => role.name === roleName)
  }

  // Check if user is in required division (HR, Finance, etc.)
  const isInDivision = (divisionName: string) => {
    if (!user?.division_name) return false
    return user.division_name.toLowerCase() === divisionName.toLowerCase()
  }

  // Check if user has direct reports (is a manager of someone)
  const hasDirectReports = () => {
    return (user?.direct_reports_count ?? 0) > 0
  }

  // Check if user has access based on roles OR divisions OR direct reports
  const hasAccess = (requiredRoles?: string[], requiredDivisions?: string[], requiresDirectReports?: boolean) => {
    // If no requirements, everyone has access
    if ((!requiredRoles || requiredRoles.length === 0) &&
        (!requiredDivisions || requiredDivisions.length === 0) &&
        !requiresDirectReports) {
      return true
    }

    // Check if user has any required role
    const hasRequiredRole = requiredRoles?.some((role) => hasRole(role)) || false

    // Check if user is in any required division
    const hasRequiredDivision = requiredDivisions?.some((div) => isInDivision(div)) || false

    // Check if user has direct reports (when required)
    const meetsDirectReportsRequirement = requiresDirectReports ? hasDirectReports() : false

    // User needs at least one matching: role OR division OR direct reports
    return hasRequiredRole || hasRequiredDivision || meetsDirectReportsRequirement
  }

  // Check if path is active
  const isPathActive = (itemPath: string) => {
    if (location.pathname === itemPath) return true
    if (itemPath !== '/' && location.pathname.startsWith(itemPath + '/')) {
      return true
    }
    return false
  }

  // Check if exact path match (for sub-items)
  const isExactPathActive = (itemPath: string) => {
    return location.pathname === itemPath
  }

  // Check if group is active (any sub-item is active)
  const isGroupActive = (group: NavGroup) => {
    return location.pathname.startsWith(group.basePath)
  }

  // Toggle group expansion
  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((name) => name !== groupName)
        : [...prev, groupName]
    )
  }

  // Filter group items based on role, division, and direct reports
  const getVisibleGroupItems = (group: NavGroup) => {
    return group.items.filter((item) => hasAccess(item.requiredRoles, item.requiredDivisions, item.requiresDirectReports))
  }

  // Check if group should be visible (has at least one visible item)
  const isGroupVisible = (group: NavGroup) => {
    return getVisibleGroupItems(group).length > 0
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed left-0 top-16 bottom-0 w-64 bg-gradient-to-b from-gray-900 to-gray-800 shadow-2xl z-30 overflow-y-auto transition-transform duration-300 ease-in-out',
          {
            'translate-x-0': isOpen,
            '-translate-x-full md:translate-x-0': !isOpen,
          }
        )}
      >
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="md:hidden absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          aria-label="Close menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <nav className="p-4 pt-12 md:pt-4">
          <ul className="space-y-1">
          {navEntries.map((entry) => {
            if (isNavGroup(entry)) {
              // Render group with sub-items
              if (!isGroupVisible(entry)) return null

              const isExpanded = expandedGroups.includes(entry.name)
              const groupActive = isGroupActive(entry)
              const visibleItems = getVisibleGroupItems(entry)

              return (
                <li key={entry.name}>
                  <button
                    onClick={() => toggleGroup(entry.name)}
                    className={clsx(
                      'w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200',
                      {
                        'bg-gray-700 text-white': groupActive,
                        'text-gray-300 hover:bg-gray-700 hover:text-white': !groupActive,
                      }
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {entry.icon}
                      <span className="font-medium">{entry.name}</span>
                    </div>
                    <svg
                      className={clsx('w-4 h-4 transition-transform duration-200', {
                        'rotate-180': isExpanded,
                      })}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isExpanded && (
                    <ul className="mt-1 ml-4 space-y-1">
                      {visibleItems.map((item) => (
                        <li key={item.path}>
                          {/* Show group label divider if present */}
                          {item.groupLabel && (
                            <div className="flex items-center gap-2 px-4 py-2 mt-2 mb-1">
                              <div className="flex-1 h-px bg-gray-600" />
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {item.groupLabel}
                              </span>
                              <div className="flex-1 h-px bg-gray-600" />
                            </div>
                          )}
                          <Link
                            to={item.path}
                            onClick={onClose}
                            className={clsx(
                              'flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 text-sm',
                              {
                                'bg-red-600 text-white shadow-lg shadow-red-500/50':
                                  isExactPathActive(item.path),
                                'text-gray-400 hover:bg-gray-700 hover:text-white':
                                  !isExactPathActive(item.path),
                              }
                            )}
                          >
                            {item.icon}
                            <span>{item.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )
            } else {
              // Render single nav item
              if (!hasAccess(entry.requiredRoles, entry.requiredDivisions, entry.requiresDirectReports)) return null

              return (
                <li key={entry.path}>
                  <Link
                    to={entry.path}
                    onClick={onClose}
                    className={clsx(
                      'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
                      {
                        'bg-red-600 text-white shadow-lg shadow-red-500/50':
                          isPathActive(entry.path),
                        'text-gray-300 hover:bg-gray-700 hover:text-white':
                          !isPathActive(entry.path),
                      }
                    )}
                  >
                    {entry.icon}
                    <span className="font-medium">{entry.name}</span>
                  </Link>
                </li>
              )
            }
          })}
        </ul>
      </nav>
    </aside>
    </>
  )
}

export default Sidebar
